import type { AsyncListData } from '@react-stately/data';
import clsx from 'clsx';
import React, { useCallback } from 'react';
import { Collection, ListBox, ListBoxItem, ListBoxLoadMoreItem, ListLayout, Virtualizer } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import type { ExtendedDynamicFormWorkflowStep } from '../../@types/forms';
import type { RecordResult } from '../../@types/records';
import { getTextColor } from '../../utils/dom-utils';
import OdinLoadingIndicator from '../common/odin-loading-indicator.component';
import TitleValueField from '../fields/linked-records/components/mobile/title-value-field';

type MobileGridBaseProps = {
  workflowSteps?: ExtendedDynamicFormWorkflowStep[] | undefined;
  list: AsyncListData<RecordResult>;
  selectionHandler: (item: RecordResult, isFullScreen: boolean) => void;
  formId?: number | undefined;
  moduleId?: number;
  allForms?: boolean;
  isLoading?: boolean;
  selectionMode?: 'single' | 'multiple' | 'none';
  customRenderItem?: (item: RecordResult) => React.JSX.Element;
};

function MobileGridBase({
  workflowSteps,
  list,
  selectionHandler,
  allForms = false,
  isLoading = false,
  selectionMode = 'single',
  customRenderItem,
}: MobileGridBaseProps) {
  const { t } = useTranslation();

  const rowIndicator = useCallback(
    (data?: RecordResult) => {
      if (workflowSteps === undefined || data === undefined) {
        return <></>;
      }
      const status = data.status;
      const backgroundColor = workflowSteps.find(
        (step: ExtendedDynamicFormWorkflowStep) => step.label === status
      )?.backgroundColor;

      const classNames = clsx('inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium');
      const colorStatusStyles = backgroundColor
        ? {
            backgroundColor: `${backgroundColor}`,
            color: getTextColor(backgroundColor),
          }
        : { backgroundColor: '#f3f4f6', color: '#374151' };

      if (status) {
        return (
          <span className={classNames} style={colorStatusStyles}>
            {status}
          </span>
        );
      }

      return <></>;
    },
    [workflowSteps]
  );

  const cardStyles = useCallback((isSelected: boolean, isHovered: boolean) => {
    return clsx(
      'cursor-pointer odin-rounded-md odin-border odin-border-gray-4 p-2 transition-all duration-200 outline-none',
      {
        'bg-primary-1 text-mono-1': isSelected,
        'border-primary-2 bg-gray-100 text-gray-800 shadow-md ring-2 ring-primary-4': isHovered,
      }
    );
  }, []);

  const renderItem = useCallback(
    (item: RecordResult) => {
      if (customRenderItem) {
        return customRenderItem(item);
      }

      const formModuleName = item.formName || item.moduleForm;
      return (
        <>
          <div className="mb-2 flex items-start justify-between">
            <div className="min-w-0 flex-1 pr-2">
              <h3 className="text-gray-900 break-words text-base font-semibold leading-tight">
                {item.title || item.docNo || 'No Title'}
              </h3>
              {item.displayText && (
                <p className="text-gray-600 mt-3 line-clamp-2 text-sm leading-relaxed">{item.displayText}</p>
              )}
            </div>
            {/* Status Badge */}
            {workflowSteps && <div className="flex-shrink-0">{rowIndicator(item)}</div>}
          </div>

          {/* Main Content Grid */}
          <div className="space-y-2">
            {allForms && (formModuleName || item.moduleName) && (
              <div className="grid grid-cols-2 gap-2 rounded-lg">
                {formModuleName && <TitleValueField title={t('form-name')} value={formModuleName} />}
                {item.moduleName && <TitleValueField title={t('module-name')} value={item.moduleName} />}
              </div>
            )}
          </div>
          <div className="mt-2 flex items-center justify-between pt-2">
            <span className="text-gray-400 text-xs">
              {t('id')}: {item.id || item.docNo || '—'}
            </span>
            {item.lastModifiedDate && (
              <span className="text-gray-400 text-xs">{new Date(item.lastModifiedDate).toLocaleDateString()}</span>
            )}
          </div>
        </>
      );
    },
    [customRenderItem, allForms, rowIndicator]
  );

  const ariaLabel = allForms ? t('all-forms-record-list') : t('record-list');

  if (list.loadingState === 'loading' || isLoading) {
    return (
      <div className="flex h-32 w-full items-center justify-center">
        <OdinLoadingIndicator />
      </div>
    );
  }

  return (
    <div className="bg-gray-50 h-full w-full flex-1 overflow-y-auto">
      <Virtualizer
        layout={ListLayout}
        layoutOptions={{
          estimatedRowHeight: 160,
          gap: 8,
          padding: 5,
        }}
      >
        <ListBox
          aria-label={ariaLabel}
          selectionMode={selectionMode}
          items={list.items}
          selectedKeys={list.selectedKeys}
          onSelectionChange={list.setSelectedKeys}
          renderEmptyState={() => (
            <div className="text-gray-500 p-6 text-center">
              <div className="text-base font-medium">{t('no-records-found')}</div>
            </div>
          )}
        >
          <Collection items={list.items}>
            {(item: RecordResult) => {
              const key = `${item.moduleForm}-${item.docNo || item.id || Math.random()}`;
              const itemId = String(item.id ?? item.docNo);
              return (
                <ListBoxItem
                  id={itemId}
                  key={key}
                  onPress={() => {
                    selectionHandler(item, false);
                  }}
                  textValue={item.displayText || 'Item'}
                  className={({ isSelected, isHovered }) => cardStyles(isSelected, isHovered)}
                >
                  {renderItem(item)}
                </ListBoxItem>
              );
            }}
          </Collection>

          {/* Load More Indicator */}
          <ListBoxLoadMoreItem
            onLoadMore={list.loadMore}
            isLoading={list.loadingState === 'loadingMore'}
            className="py-4"
          >
            <div className="flex w-full justify-center">
              <div className="bg-white odin-border odin-border-gray-4 rounded-lg p-3 shadow-sm">
                <OdinLoadingIndicator />
              </div>
            </div>
          </ListBoxLoadMoreItem>
        </ListBox>
      </Virtualizer>
    </div>
  );
}

export default MobileGridBase;
